import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import moment from "moment-timezone";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  FaArrowDown,
  FaArrowUp,
  FaChartLine,
  FaClock,
  FaDownload,
  FaEdit,
  FaExclamationCircle,
  FaPlus,
  FaRedoAlt,
  FaSave,
  FaTimes,
  FaTrash,
  FaWallet,
  FaCheckCircle,
  FaInfoCircle,
} from "react-icons/fa";
import "./CSS/CashTracing.css";

const bucketOptions = [
  { value: "opening_balance", label: "Opening Balance" },
  { value: "bank_deposit", label: "Bank Deposit" },
  { value: "reconciled_collected_cash", label: "Reconciled Collected Cash" },
  { value: "customer_credit_collection", label: "Customer Credit Cash" },
  { value: "owner_withdrawal", label: "Owner Withdrawal" },
  { value: "expense_payment", label: "Expense Payment" },
  { value: "vendor_payment", label: "Vendor Payment" },
  { value: "salary_payment", label: "Salary Payment" },
  { value: "petty_cash", label: "Petty Cash" },
  { value: "cash_in_hand", label: "Cash In Hand" },
  { value: "shortage", label: "Shortage" },
  { value: "excess", label: "Excess" },
  { value: "other", label: "Other" },
];

const directionOptions = [
  { value: "", label: "All" },
  { value: "in", label: "IN" },
  { value: "out", label: "OUT" },
];

const rangePresets = [
  { key: "1d", label: "1 Day", days: 1 },
  { key: "7d", label: "1 Week", days: 7 },
  { key: "14d", label: "2 Weeks", days: 14 },
  { key: "30d", label: "30 Days", days: 30 },
];

const defaultForm = {
  id: null,
  reconciliation_id: "",
  trace_datetime: moment().format("YYYY-MM-DDTHH:mm"),
  direction: "out",
  bucket: "bank_deposit",
  amount: "",
  reference_no: "",
  note: "",
  source_type: "manual",
  source_id: "",
};

const headersFor = (token) => ({ Authorization: `Bearer ${token}` });

const money = (value) =>
  new Intl.NumberFormat("en-IN", {
    currency: "INR",
    maximumFractionDigits: 2,
    style: "currency",
  }).format(Number(value || 0));

const bucketLabel = (bucket) =>
  bucketOptions.find((item) => item.value === bucket)?.label || bucket || "Other";

const directionLabel = (direction) => (direction === "in" ? "Inflow" : "Outflow");

const bucketTone = (bucket) => {
  const value = String(bucket || "").toLowerCase();
  if (value.includes("customer")) return "green";
  if (value.includes("reconciled")) return "green";
  if (value.includes("deposit")) return "green";
  if (value.includes("withdrawal") || value.includes("expense")) return "red";
  if (value.includes("salary") || value.includes("vendor")) return "amber";
  if (value.includes("opening")) return "blue";
  return "slate";
};

const sourceLabel = (sourceType) => {
  const value = String(sourceType || "").toLowerCase();
  if (value === "customer_credit_payment") return "Customer Credit Cash";
  if (value === "reconciliation") return "Reconciliation";
  if (value === "expense") return "Expense";
  if (value === "vendor") return "Vendor";
  if (value === "salary") return "Salary";
  if (value === "adjustment") return "Adjustment";
  return sourceType || "Manual";
};

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "error" || notice.type === "warning"
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`ct-toast ${notice.type}`}
      role={notice.type === "error" || notice.type === "warning" ? "alert" : "status"}
      aria-live={notice.type === "error" || notice.type === "warning" ? "assertive" : "polite"}
    >
      <div className="ct-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="ct-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="ct-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

function CashChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;

  return (
    <div className="ct-tooltip">
      <div className="ct-tooltip-head">
        <span>{label}</span>
        <strong>{money(point.balance)}</strong>
      </div>
      <div className="ct-tooltip-grid">
        <div>
          <em>Inflow</em>
          <strong>{money(point.inflow)}</strong>
        </div>
        <div>
          <em>Outflow</em>
          <strong>{money(point.outflow)}</strong>
        </div>
        <div>
          <em>Net move</em>
          <strong>{money(point.net)}</strong>
        </div>
        <div>
          <em>Entries</em>
          <strong>{point.entryCount}</strong>
        </div>
      </div>
      <p>
        {point.bucketBreakdown || "This point is grouped from the selected trace window."}
      </p>
    </div>
  );
}

function buildChartSeries(rows) {
  if (!rows.length) return [];

  const sorted = [...rows].sort(
    (a, b) =>
      moment(a.trace_datetime).valueOf() - moment(b.trace_datetime).valueOf()
  );

  const start = moment(sorted[0].trace_datetime);
  const end = moment(sorted[sorted.length - 1].trace_datetime);
  const spanDays = Math.max(end.diff(start, "days", true), 0.02);
  const isHourly = spanDays <= 2;
  const labelFormat = isHourly ? "DD MMM, HH:mm" : "DD MMM";

  const groups = new Map();

  for (const row of sorted) {
    const groupKey = moment(row.trace_datetime).startOf(isHourly ? "hour" : "day").toISOString();
    const label = moment(row.trace_datetime).format(labelFormat);
    const amount = Number(row.amount || 0);
    const direction = String(row.direction || "").toLowerCase();
    const current = groups.get(groupKey) || {
      balance: 0,
      bucketBreakdown: [],
      entryCount: 0,
      inflow: 0,
      label,
      net: 0,
      outflow: 0,
      rows: [],
      sortKey: moment(row.trace_datetime).valueOf(),
      groupKey,
    };

    current.entryCount += 1;
    current.rows.push(row);
    current.sortKey = Math.min(current.sortKey, moment(row.trace_datetime).valueOf());
    current.label = label;
    current.net += direction === "in" ? amount : -amount;
    if (direction === "in") {
      current.inflow += amount;
    } else {
      current.outflow += amount;
    }
    groups.set(groupKey, current);
  }

  let runningBalance = 0;

  return [...groups.values()]
    .sort((a, b) => a.sortKey - b.sortKey)
    .map((group) => {
      runningBalance += group.net;
      const first = group.rows[0];
      return {
        ...group,
        balance: runningBalance,
        bucketBreakdown:
          first && group.rows.length > 1
            ? `${group.rows.length} entries grouped from ${moment(first.trace_datetime).format(
                "DD MMM, HH:mm"
              )}`
            : first
            ? `${bucketLabel(first.bucket)} • ${directionLabel(first.direction)}`
            : "",
      };
    });
}

export default function CashTracing() {
  const token = localStorage.getItem("token");
  const rowsPerPage = 5;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    collected_cash: 0,
    manual_in: 0,
    allocated_out: 0,
    closing_balance: 0,
  });
  const [bucketRows, setBucketRows] = useState([]);
  const [filters, setFilters] = useState({
    startDate: moment().subtract(6, "days").startOf("day").format("YYYY-MM-DDTHH:mm"),
    endDate: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
    bucket: "",
    direction: "",
  });
  const [form, setForm] = useState(defaultForm);
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [selectedChartKey, setSelectedChartKey] = useState("");
  const [selectedTraceId, setSelectedTraceId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const showNotice = (type, title, message) => {
    setNotice({
      id: Date.now(),
      message,
      title,
      type,
    });
  };

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === "warning" || notice.type === "error" ? 5600 : 3800
    );
    return () => clearTimeout(timer);
  }, [notice]);

  const fetchRows = async (nextFilters = filters) => {
    const res = await api.get("/api/cash-tracing", {
      headers: headersFor(token),
      params: nextFilters,
    });
    return res.data || [];
  };

  const fetchSummary = async (nextFilters = filters) => {
    const res = await api.get("/api/cash-tracing/summary", {
      headers: headersFor(token),
      params: {
        startDate: nextFilters.startDate,
        endDate: nextFilters.endDate,
      },
    });

    return {
      summary: res.data?.summary || {},
      buckets: res.data?.buckets || [],
    };
  };

  const loadData = async (nextFilters = filters) => {
    try {
      setLoading(true);
      const [traceRows, summaryRes] = await Promise.all([
        fetchRows(nextFilters),
        fetchSummary(nextFilters),
      ]);
      setRows(traceRows);
      setSummary(summaryRes.summary);
      setBucketRows(summaryRes.buckets);
    } catch (err) {
      console.error("Cash tracing load error:", err);
      showNotice(
        "error",
        "Load failed",
        err?.response?.data?.error || "Failed to load cash tracing data."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setForm(defaultForm);
    setSelectedTraceId(null);
  };

  const openCreate = () => {
    setForm(defaultForm);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({
      ...row,
      reconciliation_id: row.reconciliation_id || "",
      source_id: row.source_id || "",
      trace_datetime: row.trace_datetime
        ? moment(row.trace_datetime).format("YYYY-MM-DDTHH:mm")
        : moment().format("YYYY-MM-DDTHH:mm"),
    });
    setShowForm(true);
  };

  const validateForm = () => {
    if (!form.trace_datetime) {
      showNotice("warning", "Time required", "Please select a date and time.");
      return false;
    }

    if (!form.amount || Number(form.amount) <= 0) {
      showNotice("warning", "Amount required", "Please enter a cash amount greater than zero.");
      return false;
    }

    return true;
  };

  const saveEntry = async () => {
    if (!validateForm()) return;

    const payload = {
      ...form,
      amount: Number(form.amount),
      reconciliation_id: form.reconciliation_id || null,
      source_id: form.source_id || null,
    };

    try {
      setSaving(true);
      if (form.id) {
        await api.put(`/api/cash-tracing/${form.id}`, payload, { headers: headersFor(token) });
        showNotice("success", "Updated", "Cash movement updated successfully.");
      } else {
        await api.post("/api/cash-tracing", payload, { headers: headersFor(token) });
        showNotice("success", "Saved", "Cash movement saved successfully.");
      }

      setShowForm(false);
      resetForm();
      await loadData();
    } catch (err) {
      console.error("Cash tracing save error:", err);
      showNotice(
        "error",
        "Save failed",
        err?.response?.data?.error || "Unable to save cash movement right now."
      );
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (row) => {
    setDeleteTarget(row);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      setSaving(true);
      await api.delete(`/api/cash-tracing/${deleteTarget.id}`, {
        headers: headersFor(token),
      });
      showNotice("success", "Deleted", "Cash movement removed.");
      setShowDelete(false);
      setDeleteTarget(null);
      await loadData();
    } catch (err) {
      console.error("Cash tracing delete error:", err);
      showNotice(
        "error",
        "Delete failed",
        err?.response?.data?.error || "Unable to delete this entry."
      );
    } finally {
      setSaving(false);
    }
  };

  const exportExcel = () => {
    const exportData = filteredRows.map((r) => ({
      DateTime: moment(r.trace_datetime).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A"),
      Direction: r.direction,
      Bucket: bucketLabel(r.bucket),
      Amount: Number(r.amount || 0),
      Reference: r.reference_no || "",
      Note: r.note || "",
      SourceType: r.source_type || "",
      ReconId: r.reconciliation_id || "",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "CashTracing");
    const buffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([buffer]), "cash_tracing.xlsx");
  };

  const applyPreset = (days) => {
    const nextFilters = {
      ...filters,
      startDate: moment().subtract(days - 1, "days").startOf("day").format("YYYY-MM-DDTHH:mm"),
      endDate: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
    };
    setFilters(nextFilters);
    loadData(nextFilters);
  };

  const resetFilters = () => {
    const nextFilters = {
      startDate: moment().subtract(6, "days").startOf("day").format("YYYY-MM-DDTHH:mm"),
      endDate: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
      bucket: "",
      direction: "",
    };
    setFilters(nextFilters);
    loadData(nextFilters);
  };

  const filteredRows = useMemo(() => {
    return rows
      .filter((row) => {
        const rowTime = moment(row.trace_datetime);
        const start = filters.startDate ? moment(filters.startDate) : null;
        const end = filters.endDate ? moment(filters.endDate) : null;
        const matchesBucket = !filters.bucket || row.bucket === filters.bucket;
        const matchesDirection = !filters.direction || row.direction === filters.direction;
        const matchesStart = !start || rowTime.isSameOrAfter(start);
        const matchesEnd = !end || rowTime.isSameOrBefore(end);
        return matchesBucket && matchesDirection && matchesStart && matchesEnd;
      })
      .sort((a, b) => moment(b.trace_datetime).valueOf() - moment(a.trace_datetime).valueOf());
  }, [filters.bucket, filters.direction, filters.endDate, filters.startDate, rows]);

  const chartSeries = useMemo(() => buildChartSeries(filteredRows), [filteredRows]);
  const totalPages = Math.max(Math.ceil(filteredRows.length / rowsPerPage), 1);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [currentPage, filteredRows]);

  useEffect(() => {
    setCurrentPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters.bucket, filters.direction, filters.endDate, filters.startDate]);

  useEffect(() => {
    if (!chartSeries.length) {
      setSelectedChartKey("");
      return;
    }

    setSelectedChartKey((prev) => {
      if (prev && chartSeries.some((item) => item.groupKey === prev)) {
        return prev;
      }
      return chartSeries[chartSeries.length - 1].groupKey;
    });
  }, [chartSeries]);

  useEffect(() => {
    if (!filteredRows.length) {
      setSelectedTraceId(null);
      return;
    }

    setSelectedTraceId((prev) => {
      if (prev && filteredRows.some((row) => String(row.id) === String(prev))) {
        return prev;
      }
      return filteredRows[0].id;
    });
  }, [filteredRows]);

  const selectedChartPoint =
    chartSeries.find((item) => item.groupKey === selectedChartKey) ||
    chartSeries[chartSeries.length - 1] ||
    null;

  const selectedTrace =
    filteredRows.find((row) => String(row.id) === String(selectedTraceId)) ||
    filteredRows[0] ||
    null;

  const flowTotals = useMemo(() => {
    return filteredRows.reduce(
      (acc, row) => {
        const amount = Number(row.amount || 0);
        if (row.direction === "in") acc.inflow += amount;
        else acc.outflow += amount;
        acc.entries += 1;
        return acc;
      },
      { entries: 0, inflow: 0, outflow: 0 }
    );
  }, [filteredRows]);

  const summaryCards = [
    {
      label: "Closing Balance",
      value: Number(summary.closing_balance || 0),
      helper: "Latest balance from the selected period",
      tone: "green",
      icon: FaWallet,
    },
    {
      label: "Collected Cash",
      value: Number(summary.collected_cash || 0),
      helper: "Reconciled cash plus customer cash settlements",
      tone: "blue",
      icon: FaArrowUp,
    },
    {
      label: "Allocated Out",
      value: Number(summary.allocated_out || 0),
      helper: "Bank deposits and outward movements",
      tone: "amber",
      icon: FaArrowDown,
    },
    {
      label: "Movement Count",
      value: flowTotals.entries,
      helper: "Filtered trace rows in view",
      tone: "slate",
      icon: FaChartLine,
    },
  ];

  const topBuckets = [...bucketRows]
    .sort((a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0))
    .slice(0, 6);

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  return (
    <div className="ct-page text-light">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="ct-hero">
        <div>
          <div className="ct-eyebrow">Finance Command</div>
          <h2>Cash tracing</h2>
          <p>
            Track movements, inspect balance shifts, and keep the cash trail clean with a
            mobile-friendly workspace that stays quick on the eyes and quick to use.
          </p>
          <div className="ct-hero-meta">
            <span>
              <FaClock aria-hidden="true" />
              {moment(filters.startDate).format("DD MMM HH:mm")} to{" "}
              {moment(filters.endDate).format("DD MMM HH:mm")}
            </span>
            <span>
              <FaChartLine aria-hidden="true" />
              {selectedChartPoint ? "Interactive balance chart ready" : "No chart data for range"}
            </span>
          </div>
        </div>

        <div className="ct-hero-actions">
          <button className="btn ct-secondary-action" type="button" onClick={loadData}>
            <FaRedoAlt aria-hidden="true" />
            Refresh
          </button>
          <button className="btn ct-secondary-action" type="button" onClick={exportExcel}>
            <FaDownload aria-hidden="true" />
            Export
          </button>
          <button className="btn ct-primary-action" type="button" onClick={openCreate}>
            <FaPlus aria-hidden="true" />
            New Movement
          </button>
        </div>
      </section>

      <section className="card-like ct-filters">
        <div className="ct-card-head">
          <div>
            <span>Time Window</span>
            <h3>Quick filters</h3>
          </div>
          <button type="button" className="btn btn-sm btn-light ct-reset-btn" onClick={resetFilters}>
            Reset
          </button>
        </div>

        <div className="ct-range-strip">
          {rangePresets.map((range) => (
            <button
              key={range.key}
              type="button"
              className="ct-range-chip"
              onClick={() => applyPreset(range.days)}
            >
              {range.label}
            </button>
          ))}
        </div>

        <div className="row g-3 align-items-end mt-1">
          <div className="col-lg-3 col-md-6">
            <label className="form-label">Start</label>
            <input
              type="datetime-local"
              className="form-control"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label className="form-label">End</label>
            <input
              type="datetime-local"
              className="form-control"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>

          <div className="col-lg-2 col-md-4">
            <label className="form-label">Bucket</label>
            <select
              className="form-select"
              value={filters.bucket}
              onChange={(e) => setFilters({ ...filters, bucket: e.target.value })}
            >
              <option value="">All</option>
              {bucketOptions.map((bucket) => (
                <option key={bucket.value} value={bucket.value}>
                  {bucket.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-2 col-md-4">
            <label className="form-label">Direction</label>
            <select
              className="form-select"
              value={filters.direction}
              onChange={(e) => setFilters({ ...filters, direction: e.target.value })}
            >
              {directionOptions.map((direction) => (
                <option key={direction.value || "all"} value={direction.value}>
                  {direction.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-lg-2 col-md-4 d-grid">
            <button className="btn btn-primary ct-apply-btn" type="button" onClick={() => loadData()}>
              Apply
            </button>
          </div>
        </div>
      </section>

      <section className="ct-summary-grid">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          return (
            <article className={`ct-summary-card ${card.tone}`} key={card.label}>
              <div className="ct-summary-icon">
                <Icon aria-hidden="true" />
              </div>
              <div className="ct-summary-copy">
                <span>{card.label}</span>
                <strong>{card.label === "Movement Count" ? card.value : money(card.value)}</strong>
                <em>{card.helper}</em>
              </div>
            </article>
          );
        })}
      </section>

      <section className="ct-grid">
        <div className="card-like ct-chart-card">
          <div className="ct-card-head">
            <div>
              <span>Cash Trail</span>
              <h3>Running balance line chart</h3>
            </div>
            <div className="ct-chart-legend">
              <span>
                <i className="line balance" />
                Balance
              </span>
              <span>
                <i className="line net" />
                Net movement
              </span>
            </div>
          </div>

          {loading ? (
            <div className="ct-empty-state">Loading cash trace data...</div>
          ) : chartSeries.length ? (
            <div className="ct-chart-shell">
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={chartSeries}
                  margin={{ top: 10, right: 18, bottom: 0, left: 0 }}
                  onClick={(state) => {
                    const point = state?.activePayload?.[0]?.payload;
                    if (point?.groupKey) {
                      setSelectedChartKey(point.groupKey);
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15, 23, 42, 0.08)" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "rgba(15, 23, 42, 0.62)", fontSize: 12, fontWeight: 700 }}
                    axisLine={{ stroke: "rgba(15, 23, 42, 0.08)" }}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    tickFormatter={(value) => `₹${Number(value).toFixed(0)}`}
                    tick={{ fill: "rgba(15, 23, 42, 0.62)", fontSize: 12, fontWeight: 700 }}
                    axisLine={{ stroke: "rgba(15, 23, 42, 0.08)" }}
                    tickLine={false}
                  />
                  <Tooltip content={<CashChartTooltip />} />
                  <Legend />
                  <ReferenceLine y={0} stroke="rgba(15, 23, 42, 0.18)" strokeDasharray="4 4" />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    stroke="#2563eb"
                    strokeWidth={3}
                    dot={{ r: 2.5, strokeWidth: 2, fill: "#fff" }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="net"
                    stroke="#16a34a"
                    strokeWidth={2}
                    strokeDasharray="7 5"
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="ct-empty-state">No cash movements found for the selected filter.</div>
          )}
        </div>

        <div className="ct-side-stack">
          <div className="card-like ct-side-card">
            <div className="ct-card-head">
              <div>
                <span>Selected Point</span>
                <h3>Trace detail</h3>
              </div>
            </div>

            {selectedChartPoint ? (
              <div className="ct-point-card">
                <div>
                  <strong>{selectedChartPoint.label}</strong>
                  <span>{selectedChartPoint.bucketBreakdown}</span>
                </div>

                <div className="ct-point-grid">
                  <div>
                    <em>Balance</em>
                    <strong>{money(selectedChartPoint.balance)}</strong>
                  </div>
                  <div>
                    <em>Inflow</em>
                    <strong>{money(selectedChartPoint.inflow)}</strong>
                  </div>
                  <div>
                    <em>Outflow</em>
                    <strong>{money(selectedChartPoint.outflow)}</strong>
                  </div>
                  <div>
                    <em>Entries</em>
                    <strong>{selectedChartPoint.entryCount}</strong>
                  </div>
                </div>
              </div>
            ) : (
              <div className="ct-empty-inline">Pick a point on the chart to inspect it.</div>
            )}
          </div>

          <div className="card-like ct-side-card">
            <div className="ct-card-head">
              <div>
                <span>Bucket Mix</span>
                <h3>Most active categories</h3>
              </div>
            </div>

            <div className="ct-bucket-list">
              {topBuckets.length ? (
                topBuckets.map((row) => (
                  <button
                    key={`${row.bucket}-${row.direction}`}
                    type="button"
                    className="ct-bucket-row"
                    onClick={() => {
                      const nextFilters = {
                        ...filters,
                        bucket: row.bucket,
                        direction: row.direction || "",
                      };
                      setFilters(nextFilters);
                      loadData(nextFilters);
                    }}
                  >
                    <span className={`ct-bucket-dot ${bucketTone(row.bucket)}`} />
                    <div className="ct-bucket-copy">
                      <strong>{bucketLabel(row.bucket)}</strong>
                      <em>{directionLabel(row.direction)}</em>
                    </div>
                    <b>{money(row.total_amount)}</b>
                  </button>
                ))
              ) : (
                <div className="ct-empty-inline">No bucket summary for this range.</div>
              )}
            </div>
          </div>

          <div className="card-like ct-side-card">
            <div className="ct-card-head">
              <div>
                <span>Live Snapshot</span>
                <h3>Current selection</h3>
              </div>
            </div>

            {selectedTrace ? (
              <div className="ct-trace-snapshot">
                <div className="ct-snapshot-row">
                  <span>Date</span>
                  <strong>{moment(selectedTrace.trace_datetime).format("DD MMM YYYY, hh:mm A")}</strong>
                </div>
                <div className="ct-snapshot-row">
                  <span>Bucket</span>
                  <strong>{bucketLabel(selectedTrace.bucket)}</strong>
                </div>
                <div className="ct-snapshot-row">
                  <span>Direction</span>
                  <strong>{directionLabel(selectedTrace.direction)}</strong>
                </div>
                <div className="ct-snapshot-row">
                  <span>Amount</span>
                  <strong>{money(selectedTrace.amount)}</strong>
                </div>
                <div className="ct-snapshot-row">
                  <span>Source</span>
                  <strong>{sourceLabel(selectedTrace.source_type)}</strong>
                </div>
              </div>
            ) : (
              <div className="ct-empty-inline">Select a trace from the table to inspect it here.</div>
            )}
          </div>
        </div>
      </section>

      <section className="card-like ct-table-card">
        <div className="ct-card-head">
          <div>
            <span>Movements</span>
            <h3>Cash trace entries</h3>
          </div>
          <div className="ct-table-total">
            <span>
              {filteredRows.length} rows • Page {Math.min(currentPage, totalPages)} of {totalPages}
            </span>
            <strong>{money(summary.closing_balance)}</strong>
          </div>
        </div>

        <div className="table-responsive ct-table-wrap">
          <table className="table ct-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Direction</th>
                <th>Bucket</th>
                <th>Amount</th>
                <th>Reference</th>
                <th>Note</th>
                <th>Source</th>
                <th>Recon ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.map((row) => {
                const isSelected = String(selectedTraceId) === String(row.id);
                return (
                  <tr
                    key={row.id}
                    className={isSelected ? "is-selected" : ""}
                    onClick={() => setSelectedTraceId(row.id)}
                  >
                    <td data-label="Date">
                      {moment(row.trace_datetime).tz("Asia/Kolkata").format("DD-MM-YYYY hh:mm A")}
                    </td>
                    <td data-label="Direction">
                      <span className={`ct-dir-pill ${row.direction === "in" ? "in" : "out"}`}>
                        {directionLabel(row.direction)}
                      </span>
                    </td>
                    <td data-label="Bucket">{bucketLabel(row.bucket)}</td>
                    <td data-label="Amount">{money(row.amount)}</td>
                    <td data-label="Reference">{row.reference_no || "-"}</td>
                    <td data-label="Note">{row.note || "-"}</td>
                    <td data-label="Source">{sourceLabel(row.source_type)}</td>
                    <td data-label="Recon ID">{row.reconciliation_id || "-"}</td>
                    <td data-label="Actions" className="ct-row-actions">
                      {row.source_type === "customer_credit_payment" ? (
                        <span className="ct-source-lock">System source</span>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit(row);
                            }}
                          >
                            <FaEdit aria-hidden="true" />
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              requestDelete(row);
                            }}
                          >
                            <FaTrash aria-hidden="true" />
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!filteredRows.length && (
                <tr>
                  <td colSpan="9">
                    <div className="ct-empty-table">
                      <FaWallet aria-hidden="true" />
                      <strong>No cash trace entries found</strong>
                      <span>Try a wider date range or clear a bucket filter.</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredRows.length > 0 && (
          <div className="ct-pagination-bar">
            <button
              type="button"
              className="ct-page-btn"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Prev
            </button>

            <div className="ct-page-list">
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  className={`ct-page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}
            </div>

            <button
              type="button"
              className="ct-page-btn"
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {showForm && (
        <div className="ct-overlay">
          <div className="ct-modal">
            <div className="ct-modal-head">
              <div>
                <span>{form.id ? "Update" : "New"}</span>
                <h3>{form.id ? "Edit cash movement" : "Record cash movement"}</h3>
              </div>
              <button
                type="button"
                className="ct-modal-close"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>

            <div className="ct-modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">DateTime</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={form.trace_datetime}
                    onChange={(e) => setForm({ ...form, trace_datetime: e.target.value })}
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Direction</label>
                  <select
                    className="form-select"
                    value={form.direction}
                    onChange={(e) => setForm({ ...form, direction: e.target.value })}
                  >
                    <option value="in">IN</option>
                    <option value="out">OUT</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Bucket</label>
                  <select
                    className="form-select"
                    value={form.bucket}
                    onChange={(e) => setForm({ ...form, bucket: e.target.value })}
                  >
                    {bucketOptions.map((bucket) => (
                      <option key={bucket.value} value={bucket.value}>
                        {bucket.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="Enter cash amount"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Reference No</label>
                  <input
                    className="form-control"
                    value={form.reference_no}
                    onChange={(e) => setForm({ ...form, reference_no: e.target.value })}
                    placeholder="Optional reference"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Recon ID</label>
                  <input
                    className="form-control"
                    value={form.reconciliation_id}
                    onChange={(e) => setForm({ ...form, reconciliation_id: e.target.value })}
                    placeholder="Optional reconciliation id"
                  />
                </div>

                <div className="col-md-8">
                  <label className="form-label">Note</label>
                  <input
                    className="form-control"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="Optional note"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Source Type</label>
                  <select
                    className="form-select"
                    value={form.source_type}
                    onChange={(e) => setForm({ ...form, source_type: e.target.value })}
                  >
                    <option value="manual">Manual</option>
                    <option value="reconciliation">Reconciliation</option>
                    <option value="expense">Expense</option>
                    <option value="vendor">Vendor</option>
                    <option value="salary">Salary</option>
                    <option value="adjustment">Adjustment</option>
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label">Source ID</label>
                  <input
                    className="form-control"
                    value={form.source_id}
                    onChange={(e) => setForm({ ...form, source_id: e.target.value })}
                    placeholder="Optional source id"
                  />
                </div>
              </div>
            </div>

            <div className="ct-modal-footer">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button className="btn btn-success" type="button" onClick={saveEntry} disabled={saving}>
                <FaSave aria-hidden="true" />
                {saving ? "Saving..." : form.id ? "Update Entry" : "Save Entry"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <div className="ct-overlay">
          <div className="ct-modal ct-confirm-modal">
            <div className="ct-modal-head">
              <div>
                <span>Confirm</span>
                <h3>Delete cash movement?</h3>
              </div>
              <button
                type="button"
                className="ct-modal-close"
                onClick={() => {
                  setShowDelete(false);
                  setDeleteTarget(null);
                }}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>

            <div className="ct-modal-body">
              <p className="ct-confirm-text">
                This will permanently remove <strong>{bucketLabel(deleteTarget?.bucket)}</strong>{" "}
                of <strong>{money(deleteTarget?.amount)}</strong>.
              </p>
            </div>

            <div className="ct-modal-footer">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => {
                  setShowDelete(false);
                  setDeleteTarget(null);
                }}
              >
                Cancel
              </button>
              <button className="btn btn-danger" type="button" onClick={confirmDelete} disabled={saving}>
                <FaTrash aria-hidden="true" />
                {saving ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
